import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  LinearProgress,
  Paper,
  Radio,
  RadioGroup,
  FormControlLabel,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useParams, useSearchParams } from "react-router-dom";
import {
  usePublicSurvey,
  usePublicSurveySession,
  useSubmitPublicSurveyAnswers,
} from "../../hooks/useSurveys";

/** Aligns with legacy Xpro query params (`idc` = cliente, `idl` = protocolo/lead). */
function parsePositiveId(value) {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
}

function storageKey(company, slug, leadId, clientId) {
  const leadPart = leadId || "anon";
  const clientPart = clientId || "noclient";
  return `public-survey-token:${company}:${slug}:${leadPart}:${clientPart}`;
}

const SESSION_STATUS_LABELS = {
  completed: "Concluída",
  started: "Iniciada",
};

export default function PublicSurveyPage() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const company = searchParams.get("company") || "";
  const tokenFromUrl = searchParams.get("token") || "";
  const clientIdFromUrl = parsePositiveId(
    searchParams.get("idc") || searchParams.get("clientId") || searchParams.get("id_cliente")
  );
  const leadIdFromUrl = parsePositiveId(
    searchParams.get("idl") || searchParams.get("leadId") || searchParams.get("id_lead")
  );
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [responseToken, setResponseToken] = useState(() => {
    if (company && slug) {
      return (
        sessionStorage.getItem(storageKey(company, slug, leadIdFromUrl, clientIdFromUrl)) ||
        tokenFromUrl ||
        ""
      );
    }
    return tokenFromUrl || "";
  });
  const [submitted, setSubmitted] = useState(false);

  const { data: survey, isLoading, error, refetch } = usePublicSurvey(slug, company, responseToken);
  const startSessionMutation = usePublicSurveySession();
  const submitMutation = useSubmitPublicSurveyAnswers();

  useEffect(() => {
    if (company && slug && responseToken) {
      sessionStorage.setItem(
        storageKey(company, slug, leadIdFromUrl, clientIdFromUrl),
        responseToken
      );
    }
  }, [company, slug, responseToken, leadIdFromUrl, clientIdFromUrl]);

  useEffect(() => {
    if (responseToken && survey && !survey.session) {
      setResponseToken("");
      if (company && slug) {
        sessionStorage.removeItem(storageKey(company, slug, leadIdFromUrl, clientIdFromUrl));
      }
    }
  }, [responseToken, survey, company, slug, leadIdFromUrl, clientIdFromUrl]);

  const questions = survey?.questions || [];
  const currentQuestion = questions[currentQuestionIndex] || null;
  const progress = questions.length
    ? Math.round(((currentQuestionIndex + 1) / questions.length) * 100)
    : 0;

  const branding = survey?.branding || {};
  const accentColor = branding.primaryColor || branding.color || "#1976d2";
  const logoUrl = branding.logoUrl || branding.logo || null;

  const isQuestionAnswered = (question) => {
    if (question.type === "info") return true;
    const answer = answers[question.id] || {};
    if (!question.required) return true;
    if (question.type === "multiple_choice") {
      const values = Array.isArray(answer.optionValue) ? answer.optionValue : [];
      return values.length > 0;
    }
    return Boolean(
      answer.optionId ||
        (answer.optionValue != null && answer.optionValue !== "" && !Array.isArray(answer.optionValue)) ||
        (answer.answerText && String(answer.answerText).trim()) ||
        answer.answerNumber != null
    );
  };

  const completionText = useMemo(() => {
    if (!survey) return "";
    return `${survey.estimatedMinutes || 2} min`;
  }, [survey]);
  const answeredCount = useMemo(
    () => questions.filter((question) => isQuestionAnswered(question)).length,
    [questions, answers]
  );
  const nextPendingIndex = useMemo(
    () => questions.findIndex((question) => !isQuestionAnswered(question)),
    [questions, answers]
  );

  const handleStart = () => {
    startSessionMutation.mutate(
      {
        slug,
        company,
        payload: {
          responseToken: tokenFromUrl || responseToken || undefined,
          // Mirror legacy: idc/idl from the closing-survey link bind cliente + protocolo.
          clientId: clientIdFromUrl || undefined,
          leadId: leadIdFromUrl || undefined,
          idc: clientIdFromUrl || undefined,
          idl: leadIdFromUrl || undefined,
          sourceChannel: "public_web",
          localeCode: "pt-BR",
        },
      },
      {
        onSuccess: (result) => {
          const nextToken = result?.data?.responseToken || "";
          setResponseToken(nextToken);
          refetch();
        },
      }
    );
  };

  const updateAnswer = (questionId, value) => {
    setAnswers((prev) => ({
      ...prev,
      [questionId]: {
        ...prev[questionId],
        ...value,
      },
    }));
  };

  const handleSubmit = () => {
    const payload = questions.flatMap((question) => {
      const answer = answers[question.id] || {};
      if (question.type === "info") return [];

      if (question.type === "multiple_choice") {
        const values = Array.isArray(answer.optionValue) ? answer.optionValue : [];
        return values.map((value) => {
          const option =
            (question.options || []).find((item) => String(item.value) === String(value)) || null;
          return {
            questionId: question.id,
            optionId: option?.id || null,
            optionValue: value,
            answerText: option?.label || String(value),
            answerScore: option?.score ?? null,
          };
        });
      }

      return [
        {
          questionId: question.id,
          optionId: answer.optionId,
          optionValue: answer.optionValue,
          answerText: answer.answerText,
          answerNumber: answer.answerNumber,
          answerScore: answer.answerScore,
        },
      ];
    });

    submitMutation.mutate(
      {
        slug,
        company,
        token: responseToken,
        answers: payload,
      },
      {
        onSuccess: () => setSubmitted(true),
      }
    );
  };

  const jumpToNextPending = () => {
    if (nextPendingIndex >= 0) {
      setCurrentQuestionIndex(nextPendingIndex);
    }
  };

  const renderQuestionInput = (question) => {
    const answer = answers[question.id] || {};
    if (question.type === "info") {
      return (
        <Typography variant="body2" color="text.secondary">
          {question.description || "Continue para a próxima etapa."}
        </Typography>
      );
    }
    if (question.type === "nps") {
      return (
        <ToggleButtonGroup
          exclusive
          value={answer.answerNumber ?? null}
          onChange={(_, value) =>
            updateAnswer(question.id, { answerNumber: value, answerScore: value })
          }
          sx={{ flexWrap: "wrap", gap: 1 }}
        >
          {Array.from(
            { length: Number(question.scaleMax || 10) - Number(question.scaleMin || 0) + 1 },
            (_, index) => Number(question.scaleMin || 0) + index
          ).map((score) => (
            <ToggleButton key={score} value={score}>
              {score}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      );
    }

    if (question.type === "rating_scale" || question.type === "emoji_scale") {
      return (
        <ToggleButtonGroup
          exclusive
          value={answer.answerNumber ?? null}
          onChange={(_, value) =>
            updateAnswer(question.id, { answerNumber: value, answerScore: value })
          }
          sx={{ flexWrap: "wrap", gap: 1 }}
        >
          {(question.options || []).map((option) => (
            <ToggleButton
              key={option.id || option.value}
              value={option.score ?? Number(option.value)}
            >
              {question.type === "emoji_scale"
                ? ["😡", "🙁", "😐", "🙂", "😍"][Number(option.position || 1) - 1] || option.label
                : option.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      );
    }

    if (question.type === "multiple_choice") {
      const values = Array.isArray(answer.optionValue) ? answer.optionValue : [];
      return (
        <Stack spacing={1}>
          {(question.options || []).map((option) => (
            <FormControlLabel
              key={option.id || option.value}
              control={
                <Checkbox
                  checked={values.includes(option.value)}
                  onChange={(event) => {
                    const nextValues = event.target.checked
                      ? [...values, option.value]
                      : values.filter((item) => item !== option.value);
                    updateAnswer(question.id, {
                      optionValue: nextValues,
                      answerText: nextValues.join(", "),
                    });
                  }}
                />
              }
              label={option.label}
            />
          ))}
        </Stack>
      );
    }

    if (question.type === "single_choice" || question.type === "yes_no") {
      return (
        <RadioGroup
          value={answer.optionId || answer.optionValue || ""}
          onChange={(event) => {
            const option =
              (question.options || []).find(
                (item) =>
                  String(item.id) === String(event.target.value) ||
                  String(item.value) === String(event.target.value)
              ) || null;
            updateAnswer(question.id, {
              optionId: option?.id || null,
              optionValue: option?.value || event.target.value,
              answerScore: option?.score ?? null,
            });
          }}
        >
          {(question.options || []).map((option) => (
            <FormControlLabel
              key={option.id || option.value}
              value={option.id || option.value}
              control={<Radio />}
              label={option.label}
            />
          ))}
        </RadioGroup>
      );
    }

    return (
      <TextField
        fullWidth
        multiline={question.type === "long_text"}
        minRows={question.type === "long_text" ? 4 : 1}
        placeholder="Digite sua resposta"
        value={answer.answerText || ""}
        onChange={(event) => updateAnswer(question.id, { answerText: event.target.value })}
      />
    );
  };

  if (!company) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error">
          Selecione o workspace correto para responder esta pesquisa. Entre em contato com quem
          enviou o link se o problema persistir.
        </Alert>
      </Container>
    );
  }

  if (isLoading) {
    return (
      <Container maxWidth="sm" sx={{ py: 10, textAlign: "center" }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !survey) {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Alert severity="error">
          {error?.message || "Não foi possível carregar esta pesquisa."}
        </Alert>
      </Container>
    );
  }

  if (submitted || survey.session?.status === "completed") {
    return (
      <Container maxWidth="sm" sx={{ py: 6 }}>
        <Card variant="outlined">
          <CardContent sx={{ textAlign: "center", py: 5 }}>
            <Typography variant="h4">{survey.thankYouTitle}</Typography>
            <Typography color="text.secondary" sx={{ mt: 2 }}>
              {survey.thankYouMessage}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Obrigado pelo seu tempo. Sua resposta já foi registrada com sucesso.
            </Typography>
          </CardContent>
        </Card>
      </Container>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: branding.backgroundColor || "#f6f8fb", py: 4 }}>
      <Container maxWidth="sm">
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, borderTop: `4px solid ${accentColor}` }}>
          <Stack spacing={2}>
            {logoUrl ? (
              <Box component="img" src={logoUrl} alt="" sx={{ maxHeight: 48, width: "auto" }} />
            ) : null}
            <Stack spacing={1}>
              <Typography variant="h4">{survey.title}</Typography>
              <Typography color="text.secondary">{survey.description}</Typography>
              <Stack direction="row" spacing={1}>
                <Chip label={`Tempo estimado: ${completionText}`} variant="outlined" />
                <Chip label={`${questions.length} perguntas`} variant="outlined" />
                <Chip label={`${answeredCount}/${questions.length} respondidas`} variant="outlined" />
              </Stack>
            </Stack>

            {!responseToken ? (
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="subtitle1">Vamos começar?</Typography>
                  <Typography color="text.secondary" sx={{ mt: 1, mb: 2 }}>
                    Você pode responder no seu ritmo. Se sair desta página, retomamos de onde parou
                    neste mesmo dispositivo.
                  </Typography>
                  <Button
                    variant="contained"
                    sx={{ bgcolor: accentColor, "&:hover": { bgcolor: accentColor } }}
                    onClick={handleStart}
                    disabled={startSessionMutation.isLoading}
                  >
                    {startSessionMutation.isLoading ? "Iniciando..." : "Começar pesquisa"}
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card variant="outlined">
                <CardContent>
                  <Stack spacing={2}>
                    <Box>
                      <LinearProgress
                        variant="determinate"
                        value={progress}
                        sx={{ height: 8, borderRadius: 999, bgcolor: "action.hover", "& .MuiLinearProgress-bar": { bgcolor: accentColor } }}
                      />
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                        Pergunta {Math.min(currentQuestionIndex + 1, questions.length)} de {questions.length} —{" "}
                        {progress}% concluído
                      </Typography>
                    </Box>

                    {currentQuestion && (
                      <>
                        <Box>
                          <Typography variant="subtitle1">
                            {currentQuestionIndex + 1}. {currentQuestion.title}
                            {currentQuestion.required ? " *" : ""}
                          </Typography>
                          {currentQuestion.description ? (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              {currentQuestion.description}
                            </Typography>
                          ) : null}
                          {currentQuestion.required && currentQuestion.type !== "info" ? (
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: "block" }}>
                              Pergunta obrigatória
                            </Typography>
                          ) : null}
                        </Box>

                        {renderQuestionInput(currentQuestion)}
                        {!isQuestionAnswered(currentQuestion) ? (
                          <Alert severity="info" sx={{ py: 0 }}>
                            Responda esta pergunta para continuar.
                          </Alert>
                        ) : null}

                        <Stack direction="row" justifyContent="space-between">
                          <Button
                            onClick={() =>
                              setCurrentQuestionIndex((prev) => Math.max(0, prev - 1))
                            }
                            disabled={currentQuestionIndex === 0}
                          >
                            Voltar
                          </Button>

                          {currentQuestionIndex < questions.length - 1 ? (
                            <Button
                              variant="contained"
                              sx={{ bgcolor: accentColor, "&:hover": { bgcolor: accentColor } }}
                              disabled={!isQuestionAnswered(currentQuestion)}
                              onClick={() =>
                                setCurrentQuestionIndex((prev) =>
                                  Math.min(questions.length - 1, prev + 1)
                                )
                              }
                            >
                              Continuar
                            </Button>
                          ) : (
                            <Stack direction="row" spacing={1}>
                              {nextPendingIndex >= 0 ? (
                                <Button variant="outlined" onClick={jumpToNextPending}>
                                  Revisar pendências
                                </Button>
                              ) : null}
                              <Button
                                variant="contained"
                                sx={{ bgcolor: accentColor, "&:hover": { bgcolor: accentColor } }}
                                onClick={handleSubmit}
                                disabled={submitMutation.isLoading || !isQuestionAnswered(currentQuestion)}
                              >
                                {submitMutation.isLoading ? "Enviando..." : "Concluir pesquisa"}
                              </Button>
                            </Stack>
                          )}
                        </Stack>
                      </>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            )}
          </Stack>
        </Paper>
      </Container>
    </Box>
  );
}
